import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User, Patient } from '../types';
import { canViewPatient } from '../utils/roleUtils';

export function useMilestones(
  user: User | null,
  patients: Patient[],
  showAlert: (message: string) => void
) {
  const [todayMilestones, setTodayMilestones] = useState<any[]>([]);
  const [isTodayMilestonesLoading, setIsTodayMilestonesLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userPrograms, setUserPrograms] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userGroups, setUserGroups] = useState<string[]>([]);

  // Helper function to load user's programs and groups
  const loadUserAssignments = useCallback(async () => {
    if (!user) return { programs: [], groups: [] };
    
    try {
      // Load programs where user is a manager
      const programsSnapshot = await getDocs(collection(db, 'programs'));
      const programs = programsSnapshot.docs
        .filter(docSnap => {
          const data = docSnap.data();
          return Array.isArray(data.managers) && data.managers.includes(user.id);
        })
        .map(docSnap => docSnap.id);
      
      // Load groups where user is a manager
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const groups = groupsSnapshot.docs
        .filter(docSnap => {
          const data = docSnap.data();
          return Array.isArray(data.managers) && data.managers.includes(user.id);
        })
        .map(docSnap => docSnap.id);
      
      return { programs, groups };
    } catch (error) {
      console.error('Error loading user assignments:', error);
      return { programs: [], groups: [] };
    }
  }, [user]);

  // Load all milestones to calculate treatment completion percentage
  const loadAllMilestones = useCallback(async () => {
    if (!user) return [];
    
    try {
      // Load user assignments FIRST to ensure they're available for filtering
      const { programs, groups } = await loadUserAssignments();
      
      const milestonesRef = collection(db, 'milestones');
      const q = query(milestonesRef, orderBy('createdAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const allMilestones = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          description: data.description,
          progress: data.progress,
          targetDate: data.targetDate || '',
          status: data.status || 'new',
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          caseId: data.caseId
        };
      });
      
      // Apply role-based filtering using the new permission system with fresh data
      const filteredMilestones = allMilestones.filter(milestone => {
        // Find the patient associated with this milestone
        const patient = patients.find(p => p.caseId === milestone.caseId);
        if (!patient) return false; // If patient not found, don't show milestone
        
        // Use the permission system to check if user can view this patient
        return canViewPatient(user, patient, programs, groups);
      });
      
      return filteredMilestones;
    } catch (error) {
      console.error('Error loading all milestones:', error);
      return [];
    }
  }, [user, patients, loadUserAssignments]);

  // Fetch today's milestones across all cases
  const loadTodayMilestones = useCallback(async () => {
    if (!user) return;
    
    setIsTodayMilestonesLoading(true);
    try {
      // Load user assignments FIRST to ensure they're available for filtering
      const { programs, groups } = await loadUserAssignments();
      setUserPrograms(programs);
      setUserGroups(groups);
      
      // Get today's date range
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      
      const milestonesRef = collection(db, 'milestones');
      const q = query(
        milestonesRef, 
        where('createdAt', '>=', startOfDay),
        where('createdAt', '<', endOfDay),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const loadedMilestones = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          description: data.description,
          progress: data.progress,
          targetDate: data.targetDate || '',
          status: data.status || 'new',
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          caseId: data.caseId
        };
      });
      
      // Apply role-based filtering using the new permission system with fresh data
      const filteredMilestones = loadedMilestones.filter(milestone => {
        // Find the patient associated with this milestone
        const patient = patients.find(p => p.caseId === milestone.caseId);
        if (!patient) return false; // If patient not found, don't show milestone
        
        // Use the permission system to check if user can view this patient
        return canViewPatient(user, patient, programs, groups);
      });
      
      setTodayMilestones(filteredMilestones);
    } catch (error) {
      console.error('Error loading today\'s milestones:', error);
    } finally {
      setIsTodayMilestonesLoading(false);
    }
  }, [user, patients, loadUserAssignments]);

  const handleDeleteMilestone = useCallback(async (milestoneId: string) => {
    try {
      const milestoneRef = doc(db, 'milestones', milestoneId);
      await deleteDoc(milestoneRef);
      
      // Refresh today's milestones
      await loadTodayMilestones();
      
      console.log('Milestone deleted successfully');
    } catch (error) {
      console.error('Error deleting milestone:', error);
      showAlert('Failed to delete milestone. Please try again.');
    }
  }, [loadTodayMilestones, showAlert]);

  const handleUpdateMilestoneStatus = useCallback(async (milestoneId: string, newStatus: string) => {
    try {
      const milestoneRef = doc(db, 'milestones', milestoneId);
      await updateDoc(milestoneRef, { 
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      
      // Refresh today's milestones
      await loadTodayMilestones();
      
      console.log('Milestone status updated successfully');
    } catch (error) {
      console.error('Error updating milestone status:', error);
      console.error('Failed to update milestone status');
    }
  }, [loadTodayMilestones]);

  // Load today's milestones on mount
  useEffect(() => {
    loadTodayMilestones();
  }, [loadTodayMilestones]);

  return {
    todayMilestones,
    isTodayMilestonesLoading,
    loadAllMilestones,
    handleDeleteMilestone,
    handleUpdateMilestoneStatus
  };
}


